import { useCallback, useEffect, useState } from "react";
import { api } from "@/client/lib/api";
import type { ClientAgentMessage } from "@/types/agent";

export interface ConversationSummary {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  tokenCostTotal: number;
}

type LoadedMsg = { role: string; contentJson: unknown; createdAt: string };

interface UseConversationsResult {
  conversations: ConversationSummary[];
  fetchConversation: (id: string) => Promise<ClientAgentMessage[]>;
  removeFromList: (id: string) => void;
  archive: (id: string) => Promise<void>;
}

export function useConversations(makeMessageId: () => string): UseConversationsResult {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);

  useEffect(() => {
    api
      .get<{ conversations: ConversationSummary[] }>("/api/agent/conversations")
      .then((res) => setConversations(res.conversations))
      .catch(() => {
        // Non-fatal — user will still get fresh chat
      });
  }, []);

  const fetchConversation = useCallback(
    async (id: string) => {
      const res = await api.get<{ conversation: { messages: LoadedMsg[] } }>(`/api/agent/conversations/${id}`);
      return res.conversation.messages.map((m) => ({
        id: makeMessageId(),
        role: (m.role === "assistant" ? "assistant" : "user") as "user" | "assistant",
        content: typeof m.contentJson === "string" ? m.contentJson : JSON.stringify(m.contentJson),
        timestamp: new Date(m.createdAt),
      }));
    },
    [makeMessageId],
  );

  const removeFromList = useCallback((id: string) => {
    setConversations((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const archive = useCallback(
    async (id: string) => {
      await api.post(`/api/agent/conversations/${id}/archive`, {});
      removeFromList(id);
    },
    [removeFromList],
  );

  return { conversations, fetchConversation, removeFromList, archive };
}
